import { getDateTime } from '../../../helpers/utils'
import translate from '../../../i18n'
import { IVersion } from '../../../pages/versions-container/share-type'
import { Card, Col, Descriptions, Flex, Row, Table } from 'antd'
import { ColumnsType } from 'antd/lib/table'

interface IProps {
  version: IVersion
}

type VersionSnapshot = {
  field: string
  value: string
}

type VersionChange = {
  field: string
  before: string
  after: string
}

const parseValue = (value: string) =>
  value
    .trim()
    .replace(/^'(.*)'$/, '$1')
    .replace(/''/g, "'")

const parseSnapshot = (snapshot?: string): VersionSnapshot[] =>
  snapshot
    ?.split('\n')
    .map(line => line.match(/^([^:]+):\s?(.*)$/))
    .filter((match): match is RegExpMatchArray => Boolean(match))
    .map(match => ({ field: match[1], value: parseValue(match[2]) })) ?? []

const parseChanges = (changes: string): VersionChange[] => {
  const lines = changes.split('\n')
  const result: VersionChange[] = []

  lines.forEach((line, index) => {
    const match = line.match(/^([^:]+):$/)
    if (match && lines[index + 1]?.startsWith('- ') && lines[index + 2]?.startsWith('- ')) {
      result.push({
        field: match[1],
        before: parseValue(lines[index + 1].slice(2)),
        after: parseValue(lines[index + 2].slice(2)),
      })
    }
  })

  return result
}

const VersionDescriptions = ({ version }: IProps) => {
  const snapshotColumns: ColumnsType<VersionSnapshot> = [
    { title: translate('version.field'), dataIndex: 'field', key: 'field' },
    { title: translate('version.value'), dataIndex: 'value', key: 'value' },
  ]
  const changeColumns: ColumnsType<VersionChange> = [
    { title: translate('version.field'), dataIndex: 'field', key: 'field' },
    { title: translate('version.before'), dataIndex: 'before', key: 'before' },
    { title: translate('version.after'), dataIndex: 'after', key: 'after' },
  ]
  const snapshot = parseSnapshot(version.snapshot)
  const changes = parseChanges(version.changes)

  return (
    <>
      <Flex orientation="vertical" gap={16}>
        <Row gutter={16}>
          <Col span={24}>
            <Card title={translate('version.detailTitle')}>
              <Descriptions
                column={1}
                bordered
                size="small"
                styles={{
                  label: { alignItems: 'center', fontWeight: 600, width: '200px' },
                  content: { minWidth: 300 },
                }}
              >
                <Descriptions.Item label={translate('version.itemId')}>
                  {version.itemId}
                </Descriptions.Item>
                <Descriptions.Item label={translate('version.itemType')}>
                  {version.itemType}
                </Descriptions.Item>
                <Descriptions.Item label={translate('version.who')}>
                  {version.who}
                </Descriptions.Item>
                <Descriptions.Item label={translate('version.action')}>
                  {version.action}
                </Descriptions.Item>
                <Descriptions.Item label={translate('version.eventName')}>
                  {version.eventName || '-'}
                </Descriptions.Item>
                <Descriptions.Item label={translate('version.createdAt')}>
                  {version.createdAt ? getDateTime(version.createdAt) : ''}
                </Descriptions.Item>
              </Descriptions>
            </Card>
          </Col>
        </Row>
        <Row gutter={16}>
          <Col span={12}>
            <Card title={translate('version.snapshot')}>
              <Table
                rowKey="field"
                columns={snapshotColumns}
                dataSource={snapshot}
                pagination={false}
                locale={{ emptyText: translate('version.emptySnapshot') }}
                size="small"
              />
            </Card>
          </Col>
          <Col span={12}>
            <Card title={translate('version.changes')}>
              <Table
                rowKey="field"
                columns={changeColumns}
                dataSource={changes}
                pagination={false}
                locale={{ emptyText: translate('version.emptyChanges') }}
                size="small"
              />
            </Card>
          </Col>
        </Row>
      </Flex>
    </>
  )
}

export default VersionDescriptions
